import { SiteUrls } from '@tableau/shared-urls';
import * as _ from 'underscore';

import { ILensInfo } from '~src/ts/models/ILensInfo';
import { LocationTypes } from '~src/ts/models/ILocationInfo';
import { IUserInfo } from '~src/ts/models/IUserInfo';

import { Formatters, Messages } from '../../codegen/Localize';
import { Location } from '../../common/react/Location';
import { TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { IDataSourceInfo } from '../../datasource/models/IDataSourceInfo';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { searchTelemetry } from '../../search/components/SearchTelemetry';
import {
    getSessionManagementServiceInstance,
    SessionManagementService
} from '../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ContentFiltersKeys } from '../../ts/models/ContentFilters';
import { FlowListItemInfo } from '../../ts/models/FlowInfo';
import { IDataRoleInfo } from '../../ts/models/IDataRoleInfo';
import { IProjectInfo } from '../../ts/models/IProjectInfo';
import { IPublishedConnectionInfo } from '../../ts/models/IPublishedConnectionInfo';
import { IViewInfo } from '../../ts/models/IViewInfo';
import { ContentDraftStatus } from '../../ts/ServerApi/types/ContentDraft';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { IWorkbookInfo } from '../../workbook/models/IWorkbookInfo';
import { BindUI } from '../common/UIBinding';
import { SearchResult } from '../models/SearchResult';
import {
    CatalogOmniboxItem,
    IQuickLinksContentTypes,
    IUserInfoForOmni,
    MenuItemHeaderInfo,
    NavigationItem,
    NavigationItemValueType,
    OmniBoxItemType,
    OmniboxMenuItemPlaceholder,
    OmniboxMenuItemType,
    Section
} from '../models/Types';
import { SearchService } from '../services/SearchService';

export const InputDebounceMilliseconds = 200;
const OmniboxTelemetryEventSource = 'omni';
const searchBoxTarget = 'searchBox';

export class OmniboxViewModel {
    private server: SessionManagementService;
    private searchBoxHasFocus = false;

    /* The sessionId is used to track all search requests issued while the 
    user is typing out their query.  When query results are received,
    they are rendered only if the sessionId of the ViewModel matches
    the sessionId at the time the search query was issued.
    
    The sessionId is incremented whenever the user clears the input box.
    
    This mechanism prevents results from previous searches appearing
    when the user has cleared their input box with intent on querying
    with different keywords. */
    private sessionId = Date.now();

    @BindUI() public searchText = '';
    @BindUI() public showResults = false;
    @BindUI() public showNoResults = false;
    @BindUI() public showPending = false;
    @BindUI() public showQuickLinks = false;
    @BindUI() public searchResultUIVisible = false;
    @BindUI() public showCatalogingError = false;
    @BindUI() public searchResult: SearchResult = null;
    @BindUI() public quickLinksResult: IUserInfo[] = [];
    @BindUI() public quickLinksResultReady: boolean = true;
    @BindUI() public sections: Section[] = [];
    @BindUI() public isInputboxActive: boolean = false;
    @BindUI() public statusForAccessibility: string = '';
    @BindUI() public navigationList: NavigationItem[] = [];
    @BindUI() public currentNavigationItem: NavigationItem = { target: null, type: OmniBoxItemType.searchbox } as NavigationItem;
    @BindUI() public currentNavigationItemIdentifier = '';
    @BindUI() public recentSearches: string[] = [];

    public siteRole = '';

    public askingForCloseWithFocus: () => void;
    public askingForCloseWithoutFocus: () => void;

    public initialize(shouldOpenWithFocusInSearchTextbox?: boolean): void {
        this.server = getSessionManagementServiceInstance();
        this.recentSearches = this.getRecentSearches();
        searchTelemetry.ensureInitialization(this.server.isOnlineEnvironment());

        SearchService.instance.initialize();
        this.clearSearch(shouldOpenWithFocusInSearchTextbox);

        SearchService.instance.notifyAboutMoreDataUpdatesForCurrentSearch = (searchResult: SearchResult) => {
            // If there's more data update for current search, then force update the current search to refresh the view.
            if (this.searchResult === searchResult) {
                this.forceUpdateSearchResult();
            }
        };
    }

    public onUnmount() {
        this.clearSearch();
    }

    public onSearchBoxFocus() {
        this.searchBoxHasFocus = true;
        this.isInputboxActive = true;
        if (!this.searchText) {
            searchTelemetry.startSearchFlow(OmniboxTelemetryEventSource);
        }
    }

    public onSearchBoxTransitionEnd() {
        if (this.isInputboxActive && !this.searchText) {
            this.showSearchResultWithQuickLinks();
        } else if (this.isInputboxActive) {
            this.searchResultUIVisible = true;
            this.showPending = true;
            this.debouncedSearch();
        }
    }

    public onSearchBoxBlur() {
        this.searchBoxHasFocus = false;
        if (!this.searchResultUIVisible) {
            this.isInputboxActive = false;
        }
    }

    public onSearchTextChange(value: string) {
        this.searchText = value;
        this.debouncedSearch();
    }

    public onEscapePressed() {
        this.cancelSearch();
        if (this.askingForCloseWithFocus) {
            this.askingForCloseWithFocus();
        }

        // when pressing escape button , text inside inputbox will be cleared but inputbox will still have focus
        this.isInputboxActive = true;
    }

    private updateCurrentNavigationItemId() {
        let result = '';

        if (this.currentNavigationItem) {
            switch (this.currentNavigationItem.type) {
                case OmniBoxItemType.views:
                case OmniBoxItemType.workbooks:
                case OmniBoxItemType.metrics:
                case OmniBoxItemType.datasources:
                case OmniBoxItemType.projects:
                case OmniBoxItemType.users:
                case OmniBoxItemType.flows:
                case OmniBoxItemType.dataRoles:
                case OmniBoxItemType.lenses:
                case OmniBoxItemType.tables:
                case OmniBoxItemType.databases:
                case OmniBoxItemType.publishedConnections:
                    const target = this.currentNavigationItem.target as OmniboxMenuItemType;
                    result = target.id;
                    break;
                case OmniBoxItemType.sectionHeader:
                    const menuItemHeader = this.currentNavigationItem.target as MenuItemHeaderInfo;
                    result = menuItemHeader.headerType.toString();
                    break;
            }
        }

        this.currentNavigationItemIdentifier = result;
    }

    public onEnterPressed() {
        switch (this.currentNavigationItem.type) {
            case OmniBoxItemType.views:
            case OmniBoxItemType.workbooks:
            case OmniBoxItemType.metrics:
            case OmniBoxItemType.datasources:
            case OmniBoxItemType.projects:
            case OmniBoxItemType.users:
            case OmniBoxItemType.flows:
            case OmniBoxItemType.dataRoles:
            case OmniBoxItemType.lenses:
            case OmniBoxItemType.tables:
            case OmniBoxItemType.databases:
            case OmniBoxItemType.publishedConnections:
                const menuItemType = this.currentNavigationItem.type as OmniBoxItemType;
                this.onResultItemSelected(menuItemType, this.currentNavigationItem.total, this.currentNavigationItem.itemIndex + 1, this.currentNavigationItem.contentId!);
                const menuItemHref = this.itemHref(menuItemType, this.currentNavigationItem.target as OmniboxMenuItemType);
                Location.instance.setUrl(menuItemHref);
                return;

            case OmniBoxItemType.sectionHeader:
                const menuItemHeader = this.currentNavigationItem.target as MenuItemHeaderInfo;
                this.onResultHeaderSelected(menuItemHeader.headerType, this.currentNavigationItem.total);
                const seeAllHeaderHref = this.seeAllHref(menuItemHeader.headerType, this.searchText);
                Location.instance.setUrl(seeAllHeaderHref);
                return;

            case OmniBoxItemType.searchbox:
            default:
                if (FeatureFlags.recentSearches) {
                    this.addSearchToRecentSearches(this.searchText);
                }
                this.onSeeAllResultSelected();

                // get the base url
                const seeAllHref = this.seeAllHref(null, this.searchText);
                Location.instance.setUrl(seeAllHref);
                return;
        }
    }

    public onArrowLeftPressed(preventDefault: () => void) {
        if (this.currentNavigationItem.type !== OmniBoxItemType.searchbox && this.currentNavigationItem.type !== OmniBoxItemType.sectionHeader && this.searchResultUIVisible) {
            this.updateNavigationItem(this.currentNavigationItem.left);
        }

        // If the search result is visible and current navigation item is not searchbox,
        // array key should work on search result, instead of on the text box to move the cursor around.
        if (this.searchResultUIVisible && this.currentNavigationItem.type !== OmniBoxItemType.searchbox) {
            preventDefault();
        }
    }

    public onArrowRightPressed(preventDefault: () => void) {
        if (this.currentNavigationItem.type !== OmniBoxItemType.searchbox && this.currentNavigationItem.type !== OmniBoxItemType.sectionHeader && this.searchResultUIVisible) {
            this.updateNavigationItem(this.currentNavigationItem.right);
        }

        // If the search result is visible and current navigation item is not searchbox,
        // array key should work on search result, instead of on the text box to move the cursor around.
        if (this.searchResultUIVisible && this.currentNavigationItem.type !== OmniBoxItemType.searchbox) {
            preventDefault();
        }
    }

    public onArrowDownPressed(preventDefault: () => void) {
        if (this.showQuickLinks) {
            document.querySelector<HTMLDivElement>('#quickLinks-' + this.getQuickLinksSections()[1] + '-0').focus();
            preventDefault();
        }

        if (this.searchResultUIVisible) {
            this.updateNavigationItem(this.currentNavigationItem.down);
        }

        // If the search result is visible, then array key should work on search result, instead of on the text box to move the cursor around.
        if (this.searchResultUIVisible) {
            preventDefault();
        }
    }

    public onArrowUpPressed(preventDefault: () => void) {
        if (this.showQuickLinks) {
            const sections = this.getQuickLinksSections();
            document.querySelector<HTMLDivElement>('#quickLinks-' + sections[sections.length - 1] + '-0').focus();
            preventDefault();
        }

        if (this.searchResultUIVisible) {
            this.updateNavigationItem(this.currentNavigationItem.up);
        }

        // If the search result is visible, then array key should work on search result, instead of on the text box to move the cursor around.
        if (this.searchResultUIVisible) {
            preventDefault();
        }
    }

    public onTabKeyPressed(preventDefault: () => void) {
        if (this.showQuickLinks) {
            document.querySelector<HTMLDivElement>('#quickLinks-' + this.getQuickLinksSections()[1] + '-0').focus();
            preventDefault();
        }
    }

    public onWindowBlurred() {
        this.cancelSearch();
        if (this.askingForCloseWithoutFocus) {
            this.askingForCloseWithoutFocus();
        }
    }

    public onSeeAllResultSelected() {
        searchTelemetry.sendSearchResultClickedEvent({
            type: TelemetryEvents.SearchResultClicked,
            eventSource: OmniboxTelemetryEventSource,
            resultType: 'global',
            seeAll: true,
            index: -1,
            total: -1,
            searchTerm: this.searchText,
            contentId: '-1',
        });
    }

    public onResultHeaderSelected(type: OmniBoxItemType, total: number) {
        searchTelemetry.sendSearchResultClickedEvent({
            type: TelemetryEvents.SearchResultClicked,
            eventSource: OmniboxTelemetryEventSource,
            resultType: type.toString(),
            seeAll: true,
            index: -1,
            total,
            searchTerm: this.searchText,
            contentId: '-1',
        });
    }

    public onResultItemSelected(type: OmniBoxItemType, total: number, index: number, contentId: string) {
        searchTelemetry.sendSearchResultClickedEvent({
            type: TelemetryEvents.SearchResultClicked,
            eventSource: OmniboxTelemetryEventSource,
            resultType: type.toString(),
            seeAll: false,
            index,
            total,
            searchTerm: this.searchText,
            contentId,
        });

        // In some scenario like when the data source search result item is selected by ***keyboard***, the search result will be closed
        // but the focus is still in search box. In this case, we need to keep the search box activated when clearing the search.
        // In order the check whether the search box has focus, we need to defer the check to next UI refresh cycle to make sure the view model already knows
        // whether the search box has focus or not since the property about whether search box has focus is updated by html focus/blur event which we don't have control
        // about which will be called first, this function or the focus/blur event handler function.
        setTimeout(() => {
            this.clearSearch(this.searchBoxHasFocus);
        });
    }

    public seeAllHref(type: OmniBoxItemType, searchText: string): string {
        // get the base url
        let seeAllHref = this.seeAllUrlForType(type);
        // Add the search query to the url
        seeAllHref += '?' + ContentFiltersKeys.search + '=' + encodeURIComponent(searchText);
        return seeAllHref;
    }

    public buildNavigatonList(): void {
        this.navigationList = [];
        const searchBoxNavigationItem: NavigationItem = {
            sectionIndex: 0,
            itemIndex: 0,
            type: OmniBoxItemType.searchbox,
            target: searchBoxTarget,
            left: null,
            right: null,
            up: null,
            down: null,
            total: 0,
            countInSection: -1,
            countInRow: 1,
        };

        // initial navigation item will always be searchbox
        this.currentNavigationItem = searchBoxNavigationItem;
        this.navigationList.push(searchBoxNavigationItem);

        // build navigation items for headers
        this.sections.forEach((section, sectionIndex) => {
            if (section.list.total === 0) {
                return;
            }

            const headerItem: MenuItemHeaderInfo = {
                headerType: section.type,
            };

            const headerNavigationItem: NavigationItem = {
                sectionIndex: sectionIndex + 1,
                itemIndex: 0,
                type: OmniBoxItemType.sectionHeader,
                target: headerItem,
                left: null,
                right: null,
                up: null,
                down: null,
                total: section.list.total,
                contentId: '',
                countInSection: -1,
                countInRow: -1,
            };
            this.navigationList.push(headerNavigationItem);

            const numberOfItemsPerRow = this.getNumberOfItemsPerRow(section.type);

            let leftMenuItem: OmniboxMenuItemType | null = null;
            let rightMenuItem: OmniboxMenuItemType | null = null;

            // build navigation items for menu items
            section.list.items.forEach((item, index) => {
                if (shouldHorizontalDisplayItem(section.type)) {
                    rightMenuItem = section.list.items.length === index ? null : section.list.items[index + 1];
                }

                const navigationItem: NavigationItem = {
                    sectionIndex: sectionIndex + 1,
                    itemIndex: index,
                    type: section.type,
                    target: item,
                    left: leftMenuItem,
                    right: rightMenuItem,
                    up: index >= numberOfItemsPerRow ? section.list.items[index - numberOfItemsPerRow] : headerItem,
                    down: null,
                    total: section.list.total,
                    contentId: item.id,
                    countInSection: section.list.items.length,
                    countInRow: (section.list.items.length % numberOfItemsPerRow) || numberOfItemsPerRow
                };

                if (shouldHorizontalDisplayItem(section.type)) {
                    leftMenuItem = item;
                }
                this.navigationList.push(navigationItem);
            });
        });

        // append navigation items for menu items for up and down
        this.navigationList.forEach((navigationItem, index) => {
            if (navigationItem.type === OmniBoxItemType.searchbox || navigationItem.type === OmniBoxItemType.sectionHeader) {
                navigationItem.down = this.navigationList[(index + 1) % this.navigationList.length].target;
                const previousNavigationItemIndex = (index + this.navigationList.length - 1) % this.navigationList.length;

                // The following line is for figureing out up side of the navigation item of the current header navigation item.
                // The goal is that no matter how many items are in the row above the current navigation item,
                // The up side of the current navigation item will always be the left most item in the row above.
                navigationItem.up = this.navigationList[previousNavigationItemIndex - this.navigationList[previousNavigationItemIndex].countInRow + 1].target;
            } else {
                const nextItem = this.getNextSectionItem(navigationItem);
                const nextSectionItem = this.getNextSectionHeader(navigationItem);
                // first check if there is next menu item in the same section, if not
                // if not, check if there are next section and get the next section header
                // if not, assign the first navigation item, which is the searchbox
                navigationItem.down = nextItem ? nextItem.target : nextSectionItem ? this.getNextSectionHeader(navigationItem).target : this.navigationList[0].target;
            }
        });
    }

    private getNextSectionHeader(navigationItem: NavigationItem): NavigationItem | undefined {
        const getNextNavigationHeaderIndex = navigationItem.sectionIndex + 1;
        return this.navigationList.find(navigationItem => navigationItem.sectionIndex >= getNextNavigationHeaderIndex && navigationItem.type === OmniBoxItemType.sectionHeader);
    }

    private getNextSectionItem(navigationItem: NavigationItem): NavigationItem | undefined {
        const numOfItemsPerRow = this.getNumberOfItemsPerRow(navigationItem.type);
        return this.navigationList.find(item => item.sectionIndex === navigationItem.sectionIndex && item.itemIndex === navigationItem.itemIndex + numOfItemsPerRow);
    }

    private getNumberOfItemsPerRow(type: OmniBoxItemType): number {
        switch (type) {
            case OmniBoxItemType.views:
            case OmniBoxItemType.workbooks:
                return 3;

            case OmniBoxItemType.metrics:
                return 2;

            default:
                return 1;
        }
    }

    public getMenuItemToolTip(type: OmniBoxItemType, item: OmniboxMenuItemType): string {
        switch (type) {
            case OmniBoxItemType.datasources:
            case OmniBoxItemType.flows:
            case OmniBoxItemType.publishedConnections:
                const itemWithSpecialToolTip = item as IDataSourceInfo | FlowListItemInfo | IWorkbookInfo | IPublishedConnectionInfo;
                return Messages.itemWithProject({ ITEM: itemWithSpecialToolTip.name, PROJECT: itemWithSpecialToolTip.project?.name });

            case OmniBoxItemType.workbooks:
                const workbookItem = item as IWorkbookInfo;
                const locationType = workbookItem.location?.type;
                if (locationType && locationType === LocationTypes.PersonalSpace) {
                    return Messages.itemWithPersonalSpace({ ITEM: workbookItem.name });
                } else {
                    return Messages.itemWithProject({ ITEM: workbookItem.name, PROJECT: workbookItem.project?.name });
                }

            case OmniBoxItemType.views:
                const viewItem = item as IViewInfo;
                return Messages.viewWithWorkbook({ VIEW: viewItem.name, WORKBOOK: viewItem.workbook?.name });

            case OmniBoxItemType.users:
                const userItem = item as IUserInfoForOmni;
                return `${userItem.displayName} (${userItem.domainName}\\${userItem.username})`;

            case OmniBoxItemType.projects:
            case OmniBoxItemType.metrics:
            case OmniBoxItemType.tables:
            case OmniBoxItemType.dataRoles:
            case OmniBoxItemType.lenses:
            case OmniBoxItemType.databases:
            default:
                const projectItem = item as IProjectInfo | IMetricInfo | CatalogOmniboxItem | IDataRoleInfo | ILensInfo;
                return projectItem.name;
        }
    }

    public itemHref(type: OmniBoxItemType, item: OmniboxMenuItemType): string {
        const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });

        switch (type) {
            case OmniBoxItemType.datasources:
                return urls.datasourceUrl(item.id);
            case OmniBoxItemType.flows:
                const flowItem = item as FlowListItemInfo;
                if (flowItem.draftStatus === ContentDraftStatus.unpublished) {
                    return urls.flowEditorDraftUrl(flowItem.draftLuid);
                } else {
                    return urls.flowUrl(item.id);
                }
            case OmniBoxItemType.projects:
                return urls.projectUrl(item.id);
            case OmniBoxItemType.workbooks:
                return urls.workbookUrl(item.id);
            case OmniBoxItemType.users:
                const userItem = item as IUserInfoForOmni;
                return getUserFilteredSearchUrl(userItem);
            case OmniBoxItemType.tables:
                return urls.catalogTableUrl(item.id);
            case OmniBoxItemType.databases:
                return urls.catalogDatabaseUrl(item.id);
            case OmniBoxItemType.dataRoles:
                return urls.dataRoleUrl(item.id);
            case OmniBoxItemType.lenses:
                return urls.lensUrl(item.id);
            case OmniBoxItemType.metrics:
                return urls.metricUrl(item.id);
            case OmniBoxItemType.publishedConnections:
                return urls.publishedConnectionUrl(item.id);
            case OmniBoxItemType.views:
            default:
                const viewItem = item as IViewInfo;
                return urls.viewUrl(viewItem.path);
        }
    }

    public showSeeAll(type: OmniBoxItemType): boolean {
        return type !== OmniBoxItemType.users || !SearchService.instance.Server.isLimitedUserVisibilityEnforced();
    }

    public cancelSearch() {
        if (this.searchText) {
            searchTelemetry.endSearchFlowWithNoResultEvent(OmniboxTelemetryEventSource, this.searchText);
        }

        this.clearSearch();
    }

    private clearSearch(shouldKeepSearchBoxActive?: boolean) {
        this.sections = [];
        this.searchResultUIVisible = false;
        this.showPending = false;
        this.showResults = false;
        this.showNoResults = false;
        this.showQuickLinks = false;
        this.searchText = '';
        if (!shouldKeepSearchBoxActive) {
            this.isInputboxActive = false;
        }
        this.navigationList = [];
    }

    private debouncedSearch = _.debounce(() => {
        let actualSearchText = this.searchText.trim();
        this.updateResults(actualSearchText);

        if (actualSearchText) {
            searchTelemetry.sendSearchTermUpdatedEvent(OmniboxTelemetryEventSource, actualSearchText);
        }
    }, InputDebounceMilliseconds);

    private updateResults(searchText: string) {
        if (searchText) {
            // if already showing results, don't add the spinner (e.g., if the omnibox is open as the user types)
            // but if the omnibox isn't open yet, show a spinner while we fetch results
            this.showPending = !(this.showResults || this.showNoResults);
            this.showQuickLinks = false;
            this.searchResultUIVisible = true;
            const sessionId = this.sessionId;
            SearchService.instance.search(searchText).then((searchResult) => {
                // only update the omnibox if this was the most recent search
                if (sessionId === this.sessionId) {
                    this.searchResult = searchResult;
                    this.generateOmniboxMenuSections();
                    this.buildNavigatonList();
                    this.padSectionItems();
                    this.showResults = searchResult.foundResults;
                    this.showNoResults = !searchResult.foundResults;
                    this.showPending = false;
                    this.showCatalogingError = searchResult.tablesReturnedError || searchResult.databasesReturnedError;

                    this.updateStatusForAccessiblity();
                }
            });
        } else if (FeatureFlags.searchQuickLinks) {
            this.sessionId++;
            this.showSearchResultWithQuickLinks();
        } else {
            this.clearSearch();
        }

        // when text is being updated, inputbox will always have focus whether it has text or not
        this.isInputboxActive = true;

        // The accessibility related status text is rendered in an ARIA live html element.
        // The status text should be updated on the UI a little bit after the search result UI is rendered instead of render together with the search result UI.
        // Otherwise, the screen reader cannot pick up the status text though the html element content change.
        setTimeout(() => {
            this.updateStatusForAccessiblity();
        }, InputDebounceMilliseconds);
    }

    private showSearchResultWithQuickLinks() {
        this.clearSearch(true);
        this.searchResultUIVisible = true;
        this.showQuickLinks = true;

        if (FeatureFlags.searchQuickLinks && !SearchService.instance.Server.isLimitedUserVisibilityEnforced()) {
            this.showPending = true;
            this.quickLinksResultReady = false;

            return SearchService.instance.recentUsers().then(quickLinksResult => {
                if (!this.searchText) {
                    this.showPending = false;
                    //
                    //  This timeout is (unfortunately) required to have 2 renders:
                    //  1. Render the removal of loading timer, to be replaced with the owner section (showPending = False)
                    //  2. Render the owner section with results.
                    //  This creates the conditions for the animation to occur because the height of that section
                    //  goes from X pixels to 0, triggering the transition.
                    //
                    setTimeout(() => {
                        this.quickLinksResult = quickLinksResult.items.slice(0, 5);
                        this.quickLinksResultReady = true;
                        SearchService.instance.usersWithProfilePictures(this.quickLinksResult.map(u => u.id)).then(quickLinksResult => {
                            this.quickLinksResult = this.quickLinksResult.map(oldUser => quickLinksResult.items.find(newUser => oldUser.id === newUser.id));
                        });
                    });
                }
            });
        }
    }

    public getQuickLinksSections(): string[] {
        const sections = !FeatureFlags.recentSearches || this.getRecentSearches().length === 0 ? ['search-omnibox', 'content', 'user'] : ['search-omnibox', ...this.getRecentSearches().map((_, i) => `recentSearch_${i}`), 'content', 'user'];
        if (this.quickLinksResultReady && this.quickLinksResult.length === 0) {
            sections.pop();
        }
        return sections;
    }

    public getRecentSearches(): string[] {
        try {
            return JSON.parse(localStorage.getItem('recentSearches')) || [];
        } catch {
            return [];
        }
    }

    public addSearchToRecentSearches(searchText: string): void {
        const actualSearchText = searchText.trim();
        if (actualSearchText) {
            const recentSearches = [actualSearchText, ...this.getRecentSearches().filter(i => i !== actualSearchText).slice(0, 3)];
            this.recentSearches = recentSearches;
            localStorage.setItem('recentSearches', JSON.stringify(recentSearches));
        }
    }

    public removeSearchFromRecentSearches(searchText: string): void {
        const recentSearches = this.getRecentSearches().filter(i => i !== searchText);
        this.recentSearches = recentSearches;
        localStorage.setItem('recentSearches', JSON.stringify(recentSearches));
    }

    public generateOmniboxMenuSections(): void {
        if (!this.searchResult) {
            this.sections = [];
            return;
        }

        const tempSections: Section[] = [];
        const viewsSection = this.searchResult.views ? { type: OmniBoxItemType.views, list: this.searchResult.views } as Section : null;
        const metricsSection = this.searchResult.metrics ? { type: OmniBoxItemType.metrics, list: this.searchResult.metrics } as Section : null;
        const datasourcesSection = this.searchResult.datasources ? { type: OmniBoxItemType.datasources, list: this.searchResult.datasources } as Section : null;
        const workbooksSection = this.searchResult.workbooks ? { type: OmniBoxItemType.workbooks, list: this.searchResult.workbooks } as Section : null;
        const projectsSection = this.searchResult.projects ? { type: OmniBoxItemType.projects, list: this.searchResult.projects } as Section : null;
        const usersSection = this.searchResult.users ? { type: OmniBoxItemType.users, list: this.searchResult.users } as Section : null;
        const flowsSection = this.searchResult.flows ? { type: OmniBoxItemType.flows, list: this.searchResult.flows } as Section : null;
        const dataRolesSection = this.searchResult.dataRoles ? { type: OmniBoxItemType.dataRoles, list: this.searchResult.dataRoles } as Section : null;
        const lensesSection = this.searchResult.lenses ? { type: OmniBoxItemType.lenses, list: this.searchResult.lenses } as Section : null;
        const tablesSection = this.searchResult?.tables ? { type: OmniBoxItemType.tables, list: this.searchResult.tables } as Section : null;
        const databasesSection = this.searchResult?.databases ? { type: OmniBoxItemType.databases, list: this.searchResult.databases } as Section : null;
        const publishedConnectionsSection
            = this.searchResult?.publishedConnections
                ? { type: OmniBoxItemType.publishedConnections, list: this.searchResult.publishedConnections } as Section
                : null;

        switch (this.siteRole) {
            case SiteRoles.Viewer:
            case SiteRoles.Unlicensed:
            case SiteRoles.ViewerWithPublish:
            case SiteRoles.UnlicensedWithPublish:
            case SiteRoles.Guest:
                if (viewsSection) tempSections.push(viewsSection);
                if (workbooksSection) tempSections.push(workbooksSection);
                if (metricsSection) tempSections.push(metricsSection);
                if (lensesSection) tempSections.push(lensesSection);
                if (projectsSection) tempSections.push(projectsSection);
                if (usersSection) tempSections.push(usersSection);
                if (datasourcesSection) tempSections.push(datasourcesSection);
                if (publishedConnectionsSection) tempSections.push(publishedConnectionsSection);
                if (flowsSection) tempSections.push(flowsSection);
                if (dataRolesSection) tempSections.push(dataRolesSection);
                if (tablesSection) tempSections.push(tablesSection);
                if (databasesSection) tempSections.push(databasesSection);
                break;
            case SiteRoles.ServerAdministrator:
            case SiteRoles.SiteAdministratorCreator:
            case SiteRoles.SiteAdministratorExplorer:
            case SiteRoles.SupportUser:
            case SiteRoles.Creator:
            case SiteRoles.Explorer:
            case SiteRoles.ExplorerCanPublish:
            default:
                if (viewsSection) tempSections.push(viewsSection);
                if (workbooksSection) tempSections.push(workbooksSection);
                if (metricsSection) tempSections.push(metricsSection);
                if (lensesSection) tempSections.push(lensesSection);
                if (datasourcesSection) tempSections.push(datasourcesSection);
                if (publishedConnectionsSection) tempSections.push(publishedConnectionsSection);
                if (projectsSection) tempSections.push(projectsSection);
                if (usersSection) tempSections.push(usersSection);
                if (flowsSection) tempSections.push(flowsSection);
                if (dataRolesSection) tempSections.push(dataRolesSection);
                if (tablesSection) tempSections.push(tablesSection);
                if (databasesSection) tempSections.push(databasesSection);
                break;
        }
        this.sections = tempSections;
    }

    public padSectionItems(): void {
        /* Because the UI uses "space between" of flex justify-content to auto fit the search result UI with and
        without the scrollbar, it requires the section items always fulfill the whole row.
        It means if the row allows 3 item but there is only 2 section items, we need to add 2 placeholder item
        to fulfill the rest quota of the row.
        It's for fixing the following bug
        Defect 1224118: [Platform] [Search][ARM] When there's only 2 results, the result items are rendered at left side and right side of search result UI instead of side by side */

        this.sections.forEach(section => {
            const numberOfItemsPerRow = this.getNumberOfItemsPerRow(section.type);
            if (section.list.items.length % numberOfItemsPerRow !== 0) {
                const additionalPlaceholderCount = numberOfItemsPerRow - (section.list.items.length % numberOfItemsPerRow);
                for (let i = 0; i < additionalPlaceholderCount; i++) {
                    section.list.items.push({ type: OmniBoxItemType.placeholder } as OmniboxMenuItemPlaceholder);
                }
            }
        });
    }

    public isSectionItemPlaceholder(sectionItem: OmniboxMenuItemType): boolean {
        const placeholderItem = sectionItem as OmniboxMenuItemPlaceholder;
        return !!(placeholderItem.type && placeholderItem.type === OmniBoxItemType.placeholder);
    }

    private updateNavigationItem(navigationValue: NavigationItemValueType): void {
        const nextNavigationItem = this.findNavigationItemByTarget(navigationValue);
        this.currentNavigationItem = nextNavigationItem ? nextNavigationItem : this.currentNavigationItem;

        this.updateCurrentNavigationItemId();
    }

    private findNavigationItemByTarget(navigationValue: NavigationItemValueType) {
        return this.navigationList.find(item => item.target === navigationValue);
    }

    private seeAllUrlForType(type: OmniBoxItemType): string {
        const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });
        switch (type) {
            case OmniBoxItemType.datasources:
                return urls.searchDatasourcesUrl();
            case OmniBoxItemType.flows:
                return urls.searchFlowsUrl();
            case OmniBoxItemType.projects:
                return urls.searchProjectsUrl();
            case OmniBoxItemType.workbooks:
                return urls.searchWorkbooksUrl();
            case OmniBoxItemType.users:
                return urls.searchViewsUrl();
            case OmniBoxItemType.tables:
                return urls.searchTablesUrl();
            case OmniBoxItemType.databases:
                return urls.searchDatabasesUrl();
            case OmniBoxItemType.dataRoles:
                return urls.searchDataRolesUrl();
            case OmniBoxItemType.lenses:
                return urls.searchLensesUrl();
            case OmniBoxItemType.metrics:
                return urls.searchMetricsUrl();
            case OmniBoxItemType.publishedConnections:
                return urls.searchPublishedConnectionsUrl();
            case OmniBoxItemType.views:
                return urls.searchViewsUrl();
            case OmniBoxItemType.global:
            default:
                return FeatureFlags.searchV2 ? urls.searchUrl() : urls.searchViewsUrl();
        }
    }

    private updateStatusForAccessiblity() {
        if (this.showPending) {
            this.statusForAccessibility = Messages.omniboxFetchingResults();
        } else if (this.showNoResults) {
            this.statusForAccessibility = Messages.omniboxShowingNoResults();
        } else if (this.showResults) {
            this.statusForAccessibility = Messages.omniboxShowingResults({
                TOTAL_COUNT: this.searchResult.totalResults,
                TOTAL_COUNT_LOC: Formatters.formatCount(this.searchResult.totalResults)
            });
        } else {
            this.statusForAccessibility = '';
        }
    }

    private forceUpdateSearchResult() {
        this.searchResult = { ...this.searchResult };
    }
}

export function shouldHorizontalDisplayItem(type: OmniBoxItemType): boolean {
    return type === OmniBoxItemType.views || type === OmniBoxItemType.workbooks || type === OmniBoxItemType.metrics;
}

export function getUserFilteredSearchUrl(user: IUserInfo): string {
    const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });
    return urls.searchViewsUrl() + '?' + ContentFiltersKeys.owner + '=' + user.id + '&' + ContentFiltersKeys.search + '=';
}

export function getContentTypeUrl(content: IQuickLinksContentTypes): string {
    const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });
    return content.getPath(urls);
}

export function formatAsSearchUrl(searchText: string): string {
    const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });
    return urls.searchViewsUrl() + '?' + ContentFiltersKeys.search + '=' + searchText;
}